import { IAlias } from "ts/api/ResolveApi/resolve-api.types";
import {
  IClientPatch,
  IPatchAddAction,
  IPatchDeleteAction,
  IPatchInsertAction,
  IPatchSubstAction,
  PatchType,
} from "../../components/course-author/helpers/patches";
import { createPatchBase } from "../../components/course-author/helpers/patches/creators";
import { IAliasState, IUserAccount } from "../types/common";
import { AliasTag, EmailAddressTag } from "../AccountManagement/types";
import { PreferredLocale } from "../../utils/locales";
import { ClientFeature } from "./../../parsers/login";

export function modifyAccountTitlePatch(
  versionId: number,
  account: IUserAccount,
  currentAccountId: string,
  title: string
): IClientPatch<IPatchSubstAction<string>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: account.displayName,
        y: title,
      },
      position: "displayName",
    },
  };
}

interface IAddAccountEmailPatch {
  id: number;
  state: {
    address: string;
    tag: EmailAddressTag;
  };
}

export function addAccountEmailPatch(
  versionId: number,
  currentAccountId: string,
  email: string
): IClientPatch<IPatchAddAction<IAddAccountEmailPatch>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        at: 0,
        item: {
          id: 0,
          state: {
            address: email,
            tag: EmailAddressTag.ClaimedAddress,
          },
        },
        type: PatchType.add,
      },
      position: "emailAddresses",
    },
  };
}

interface IModifyAccountEmailPatch {
  address: string;
  tag: EmailAddressTag;
}

export function modifyAccountEmailPatch(
  versionId: number,
  currentAccountId: string,
  currentEmail: string,
  currentEmailTag: EmailAddressTag,
  newEmail: string
): IClientPatch<IPatchSubstAction<IModifyAccountEmailPatch>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: {
          address: currentEmail,
          tag: currentEmailTag,
        },
        y: {
          address: newEmail,
          tag: EmailAddressTag.ClaimedAddress,
        },
      },
      position: "emailAddresses.0.state",
    },
  };
}

export function deleteAccountEmailPatch(
  versionId: number,
  currentAccountId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: false,
        y: true,
      },
      position: "emailAddresses.0.deleted",
    },
  };
}

export function undeleteAccountEmailPatch(
  versionId: number,
  currentAccountId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: true,
        y: false,
      },
      position: "emailAddresses.0.deleted",
    },
  };
}

interface IAddAccountAliasPatch {
  id: number;
  state: {
    namespace: string;
    alias: IAlias;
    tag: { tag: string; content: [] };
  };
}

export function addAccountAliasPatch(
  versionId: number,
  currentAccountId: string,
  organizationId: string,
  alias: string,
  addPosition: number
): IClientPatch<IPatchAddAction<IAddAccountAliasPatch>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        at: addPosition,
        item: {
          id: addPosition,
          state: {
            alias: {
              kind: "",
              name: alias,
            },
            namespace: `org.${organizationId}`,
            tag: {
              content: [],
              tag: AliasTag.RequestedNewAlias,
            },
          },
        },
        type: PatchType.add,
      },
      position: "aliases",
    },
  };
}

interface IModifyAccountAliasPatch {
  alias: IAlias;
  namespace: string;
  tag: { tag: AliasTag; content: { kind: string; name: string } | [string, { kind: string; name: string }] | null };
}

export function modifyAccountAliasPatch(
  positionId: number,
  versionId: number,
  currentAccountId: string,
  organizationId: string,
  currentAliasState: IAliasState,
  newAlias: string
): IClientPatch<IPatchSubstAction<IModifyAccountAliasPatch>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: {
          ...currentAliasState,
        },
        y: {
          alias: {
            kind: "",
            name: currentAliasState.tag.content ? currentAliasState.tag.content.name : currentAliasState.alias.name,
          },
          namespace: `org.${organizationId}`,
          tag: {
            content: [
              `org.${organizationId}`,
              {
                kind: "",
                name: newAlias,
              },
            ],
            tag: AliasTag.RequestedChangeAlias,
          },
        },
      },
      position: `aliases.${positionId}.state`,
    },
  };
}

export function deleteAccountAliasPatch(
  positionId: number,
  versionId: number,
  currentAccountId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: false,
        y: true,
      },
      position: `aliases.${positionId}.deleted`,
    },
  };
}

export function undeleteAccountAliasPatch(
  positionId: number,
  versionId: number,
  currentAccountId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: true,
        y: false,
      },
      position: `aliases.${positionId}.deleted`,
    },
  };
}

export function modifyPreferredLocalePatch(
  versionId: number,
  currentAccountId: string,
  currentLocale: PreferredLocale | null,
  newLocale: PreferredLocale | null
): IClientPatch<IPatchSubstAction<PreferredLocale | null>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: currentLocale,
        y: newLocale,
      },
      position: "preferredLocale",
    },
  };
}

export function insertClientFeaturePatch(
  versionId: number,
  currentAccountId: string,
  newClientFeature: ClientFeature
): IClientPatch<IPatchInsertAction<ClientFeature>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        elem: newClientFeature,
        type: PatchType.ins,
      },
      position: "oldClientFeatures",
    },
  };
}

export function deleteClientFeaturePatch(
  versionId: number,
  currentAccountId: string,
  clientFeature: ClientFeature
): IClientPatch<IPatchDeleteAction<ClientFeature>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        elem: clientFeature,
        type: PatchType.del,
      },
      position: "oldClientFeatures",
    },
  };
}

export function modifyOwningOrgPatch(
  versionId: number,
  currentAccountId: string,
  currentOwningOrg: string,
  newOwningOrg: string
): IClientPatch<IPatchSubstAction<string>> {
  return {
    ...createPatchBase(currentAccountId, versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: currentOwningOrg,
        y: newOwningOrg,
      },
      position: "owner",
    },
  };
}
